import { tokenManager } from "../../auth/tokenManager";
import {
  AccessLevel,
  CreateGroup,
  CreatePolicy,
  Group,
  Policy,
  UserProfile,
} from "../../models/user.model";
import { MdslApi } from "./mdsl.api";

export class UserApi extends MdslApi {
  private static _userInstance: UserApi | null = null;

  constructor(getTokenFn: () => Promise<string>) {
    super(getTokenFn, undefined);
    UserApi._userInstance = this;
  }

  static resetInstanceForTest() {
    UserApi._userInstance = null;
  }

  static getInstance() {
    if (!UserApi._userInstance) {
      UserApi._userInstance = new UserApi(() => tokenManager.getToken());
    }
    return UserApi._userInstance;
  }

  async getUserProfile(): Promise<UserProfile> {
    const authToken = await this.getTokenFn();
    return this.client
      .get("/profile", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data);
  }

  async getUserGroups(): Promise<Group[]> {
    const authToken = await this.getTokenFn();
    return this.client
      .get("/groups", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data.collection);
  }

  async getGroupById(groupId: string): Promise<Group> {
    const authToken = await this.getTokenFn();
    return this.client
      .get(`/groups/${groupId}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data);
  }

  async getPolicies(): Promise<Policy[]> {
    const authToken = await this.getTokenFn();
    return this.client
      .get("/policies", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data.collection);
  }

  async getPolicyById(policyId: string): Promise<Policy> {
    const authToken = await this.getTokenFn();
    return this.client
      .get(`/policies/${policyId}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data);
  }

  async addNewPolicyToGroup(groupId: string, options: CreatePolicy): Promise<Policy> {
    const authToken = await this.getTokenFn();
    return this.client
      .post(
        `/groups/${groupId}/policies`,
        {
          ...options,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      )
      .then((res) => res.data.data);
  }

  async addNewGroup(options: CreateGroup): Promise<Group> {
    const authToken = await this.getTokenFn();
    return this.client
      .post(
        "/groups",
        {
          ...options,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      )
      .then((res) => res.data.data);
  }

  async addUserToGroup(
    groupId: string,
    userId: string,
    username: string,
    options: AccessLevel
  ): Promise<UserProfile> {
    if (!userId) {
      const user = await this.getUserByName(username);
      userId = user.uid;
    }
    const authToken = await this.getTokenFn();
    return this.client
      .post(
        `/groups/${groupId}/users/${userId}`,
        {
          ...options,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      )
      .then((res) => res.data.data);
  }

  async addNetworkToPolicyById(policyId: string | undefined, networkId: string): Promise<Policy> {
    if (!policyId) {
      throw new Error("Policy ID is required");
    }
    const authToken = await this.getTokenFn();
    return this.client
      .post(
        `/policies/${policyId}/networks/${networkId}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => res.data.data);
  }

  async getUserByName(username: string): Promise<UserProfile> {
    const authToken = await this.getTokenFn();
    return this.client
      .get(`/users/${username}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => res.data.data);
  }
}
